/** David, the maintenance coordinator: work orders, the parts shop and what to buy. The closed
    sets every screen and tool share; the API checks membership against these, never a screen. */

export const WORK_ORDER_CATEGORIES = [
  "hvac",
  "plumbing",
  "electrical",
  "appliance",
  "structural",
  "safety",
  "grounds",
  "housekeeping",
  "it",
  "other",
] as const;
export type WorkOrderCategory = (typeof WORK_ORDER_CATEGORIES)[number];

export const WORK_ORDER_CATEGORY_LABELS: Record<WorkOrderCategory, string> = {
  hvac: "Heating and cooling",
  plumbing: "Plumbing",
  electrical: "Electrical",
  appliance: "Appliance",
  structural: "Building and structure",
  safety: "Life safety",
  grounds: "Grounds",
  housekeeping: "Housekeeping",
  it: "Phones, TV and network",
  other: "Other",
};

export const WORK_ORDER_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type WorkOrderPriority = (typeof WORK_ORDER_PRIORITIES)[number];

export const WORK_ORDER_PRIORITY_LABELS: Record<WorkOrderPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

/** open: reported. in_progress: somebody is on it. waiting_parts: stopped until a part arrives.
    on_hold: stopped for another reason. completed and cancelled are closed. */
export const WORK_ORDER_STATUSES = [
  "open",
  "in_progress",
  "waiting_parts",
  "on_hold",
  "completed",
  "cancelled",
] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  waiting_parts: "Waiting for parts",
  on_hold: "On hold",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const OPEN_WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = [
  "open",
  "in_progress",
  "waiting_parts",
  "on_hold",
];

export const isOpenWorkOrder = (status: WorkOrderStatus): boolean =>
  OPEN_WORK_ORDER_STATUSES.includes(status);

/** What a work order's log records: a note, a status or assignment change, a part used, a part
    requested, what David found on the web, and the closing. */
const WORK_ORDER_EVENT_KINDS = [
  "note",
  "status",
  "assignment",
  "part_used",
  "part_requested",
  "research",
  "cost",
] as const;
export type WorkOrderEventKind = (typeof WORK_ORDER_EVENT_KINDS)[number];

export const WORK_ORDER_SOURCES = ["staff", "resident", "family", "inspection", "agent"] as const;
export type WorkOrderSource = (typeof WORK_ORDER_SOURCES)[number];

/** How the shop counts a part. */
export const PART_UNITS = [
  "each",
  "pair",
  "box",
  "pack",
  "case",
  "roll",
  "foot",
  "gallon",
  "quart",
  "pound",
  "bag",
  "tube",
  "bottle",
] as const;
export type PartUnit = (typeof PART_UNITS)[number];

export const PART_CATEGORIES = [
  "hvac",
  "plumbing",
  "electrical",
  "hardware",
  "paint",
  "lighting",
  "filters",
  "safety",
  "tools",
  "appliance",
  "other",
] as const;
export type PartCategory = (typeof PART_CATEGORIES)[number];

export const PART_CATEGORY_LABELS: Record<PartCategory, string> = {
  hvac: "Heating and cooling",
  plumbing: "Plumbing",
  electrical: "Electrical",
  hardware: "Hardware",
  paint: "Paint and finish",
  lighting: "Lighting",
  filters: "Filters",
  safety: "Life safety",
  tools: "Tools",
  appliance: "Appliance",
  other: "Other",
};

/** Why the count moved: set to a counted number, used on a work order, received from an order or
    a store run, or adjusted (found, broken, returned). */
const PART_MOVE_REASONS = ["set", "used", "received", "adjusted"] as const;
export type PartMoveReason = (typeof PART_MOVE_REASONS)[number];

/** requested: on the list, waiting for someone who may approve the spend. approved: Nancy may
    buy it. ordered: in a placed order. received: in the shop. declined and cancelled are closed. */
export const PURCHASE_REQUEST_STATUSES = [
  "requested",
  "approved",
  "ordered",
  "received",
  "declined",
  "cancelled",
] as const;
export type PurchaseRequestStatus = (typeof PURCHASE_REQUEST_STATUSES)[number];

export const PURCHASE_REQUEST_STATUS_LABELS: Record<PurchaseRequestStatus, string> = {
  requested: "Waiting for approval",
  approved: "Approved to buy",
  ordered: "Ordered",
  received: "Received",
  declined: "Declined",
  cancelled: "Cancelled",
};

/** The work order number people say aloud: WO-0001 at each building, in the order filed. */
export const workOrderNumber = (sequence: number): string =>
  `WO-${String(sequence).padStart(4, "0")}`;

/** What the parts screen asks Nancy, in her chat, when its approved list is sent to her. */
export function maintenancePartsOrderAsk(building?: string | null): string {
  return `Please order the approved maintenance parts list${building ? ` at ${building}` : ""}.`;
}

/** How a part's stock stands against its minimum: the number to buy to reach its target. */
export function partShortfall(part: {
  quantity: number;
  parMin: number | null;
  parTarget: number | null;
}): number {
  if (part.parMin === null || part.quantity >= part.parMin) return 0;
  const target = Math.max(part.parTarget ?? part.parMin, part.parMin);
  return Math.max(0, Math.ceil(target - part.quantity));
}
